/**
 * The two pure rules behind pinning a transcript card to the top of the scroll viewport, kept out of the
 * hook so both can be tested without an IntersectionObserver (jsdom has none).
 *
 * Nothing here knows about audiences: the caller supplies ordered keys, so the same mechanism can pin any
 * keyed run of cards.
 */

/**
 * How much of a card may still be visible before its pinned copy takes over. At 0.5 the handover happens
 * as the card passes half way out of view, rather than waiting for it to leave entirely — a card that is
 * mostly gone is already too small a target to be the way back into the audience.
 */
export const DEFAULT_REVEAL_RATIO = 0.5;

/**
 * True when more than `revealRatio` of the card has scrolled ABOVE the viewport's top edge.
 *
 * Measured from the rects rather than `intersectionRatio`, because the ratio cannot tell "half way off the
 * top" from "half way onto the bottom" — and pinning a card the user has not reached yet would advertise an
 * audience from further down the conversation. A zero-height rect (an unlaid-out node) is never past.
 */
export function isScrolledPast(
  rect: { top: number; height: number },
  rootTop: number,
  revealRatio = DEFAULT_REVEAL_RATIO,
): boolean {
  if (rect.height <= 0) return false;
  const hiddenAbove = rootTop - rect.top;
  return hiddenAbove > rect.height * revealRatio;
}

/**
 * The one key pinned to the top: the LAST one in transcript order that has scrolled past. A newer card
 * takes over only once it, too, scrolls past — while it is still on screen the older one stays pinned — so
 * there is never more than one, and nothing is pinned while every card is still in view.
 *
 * Driven by transcript order, not by the order the observer reported entries in.
 */
export function pickStickyKey(orderedKeys: string[], scrolledPast: ReadonlySet<string>): string | null {
  for (let i = orderedKeys.length - 1; i >= 0; i--) {
    const key = orderedKeys[i]!;
    if (scrolledPast.has(key)) return key;
  }
  return null;
}
